/**
 * Empirical quality gate for the GPS-derived accel/brake tier.
 *
 * A lap with no accelerometer qualifies for display only when its GPS stream
 * is both frequent enough and clean enough to trust as a longitudinal-
 * acceleration signal. The displayed value is the EXISTING
 * `calculateAccelerationFromGps` output — this file only decides whether to
 * trust it, it never recomputes it.
 *
 * Tuning: edit → `npm run build` in src/PitBrain.Shared → restart/rebuild the
 * clients. Values below are starting points — calibrate against real
 * PZRacing/RaceBox/phone sessions during implementation.
 */

import type { GpsPoint } from '../../domain/telemetry/processed/GpsPoint'
import type { FrequencyAdaptiveConfig } from '../../utils/FrequencyAdaptiveConfig'
import { smoothValuesGaussian } from '../../utils/smoothingUtils'

/** Unit: ms. Median interval between consecutive GPS samples in the lap must be at or below this (~7 Hz floor). Default: 150 */
export const GPS_TIER_MAX_MEDIAN_INTERVAL_MS = 150

/** Unit: km/h. RMS of (raw GPS speed − frequency-adaptive smoothed speed trend) over the lap must be at or below this. Default: 1.5 */
export const GPS_TIER_MAX_SPEED_RESIDUAL_KMH = 1.5

export type GpsAccelTierResult =
  | { qualifies: true }
  | { qualifies: false; reason: 'gps-too-sparse' | 'gps-too-noisy' }

/** Median gap (ms) between consecutive samples' elapsedMs. Infinity if < 2 points. */
export function medianSampleIntervalMs(
  points: Array<{ elapsedMs: number }>
): number {
  const deltas: number[] = []
  for (let i = 1; i < points.length; i++) {
    deltas.push(points[i].elapsedMs - points[i - 1].elapsedMs)
  }
  if (deltas.length === 0) return Infinity
  deltas.sort((a, b) => a - b)
  const mid = Math.floor(deltas.length / 2)
  return deltas.length % 2 === 0
    ? (deltas[mid - 1] + deltas[mid]) / 2
    : deltas[mid]
}

/** RMS of element-wise (a[i] − b[i]); arrays must be equal length. */
export function rmsOfDifferences(a: number[], b: number[]): number {
  if (a.length === 0) return 0
  let sumSq = 0
  for (let i = 0; i < a.length; i++) {
    const d = a[i] - b[i]
    sumSq += d * d
  }
  return Math.sqrt(sumSq / a.length)
}

/**
 * RMS (km/h) of raw speed minus its frequency-adaptive Gaussian trend, using
 * the IDENTICAL window/pass formula `gpsAccelerationCalculator.ts` applies —
 * same tool, applied to speed instead of to the derived acceleration, so the
 * residual measures exactly the noise the production accel path smooths away.
 */
export function speedResidualRmsKmh(
  speedsKmh: number[],
  frequencyConfig: FrequencyAdaptiveConfig
): number {
  const window = Math.round(8 * frequencyConfig.frequencyScale)
  const passes = Math.min(frequencyConfig.smoothingPasses, 3)
  const smoothed = smoothValuesGaussian(speedsKmh, window, passes)
  return rmsOfDifferences(speedsKmh, smoothed)
}

/**
 * Checked in order (sparsity first — a slow GPS stream also makes the residual
 * metric meaningless, since the Gaussian window is sized off the declared
 * frequency, so reporting "too noisy" on data that's actually just slow would
 * misdiagnose it):
 *  1. Median interval above GPS_TIER_MAX_MEDIAN_INTERVAL_MS → gps-too-sparse.
 *  2. Speed-residual RMS above GPS_TIER_MAX_SPEED_RESIDUAL_KMH → gps-too-noisy.
 *  3. Otherwise → qualifies.
 */
export function classifyGpsAccelTier(
  gpsPoints: GpsPoint[],
  frequencyConfig: FrequencyAdaptiveConfig
): GpsAccelTierResult {
  if (medianSampleIntervalMs(gpsPoints) > GPS_TIER_MAX_MEDIAN_INTERVAL_MS) {
    return { qualifies: false, reason: 'gps-too-sparse' }
  }
  const residual = speedResidualRmsKmh(
    gpsPoints.map((p) => p.speedKmh),
    frequencyConfig
  )
  if (residual > GPS_TIER_MAX_SPEED_RESIDUAL_KMH) {
    return { qualifies: false, reason: 'gps-too-noisy' }
  }
  return { qualifies: true }
}
